export const modulesPart1 = [
  {
    id: 1,
    slug: "marketing-fundamentals",
    title: "Marketing Fundamentals",
    color: "yellow",
    tagline: "Basics pehle clear karo, warna ad budget paani mein jayega.",
    summary: "USP, Digital vs Performance Marketing, target audience and offers.",
    sections: [
      {
        id: "what-is-pm",
        h: "What is Performance Marketing?",
        p: [
          "In Digital Marketing you learn to ==use the platforms== — Instagram posts, SEO, running ads, generating leads.",
          "In Performance Marketing you learn to ==calculate revenue from those same platforms==. You work on the company's revenue model — from generating a lead all the way to company profit.",
        ],
        hinglish: "Simple bhasha mein: ads pe ₹1 lakh lagaya aur company ko ₹3–4 lakh ka revenue laake diya — yahi Performance Marketing hai.",
        sketch: "spendToRevenue",
        points: [
          "Lower the cost of getting a lead or a sale.",
          "Scale the revenue using the right metrics.",
          "Every rupee spent should be trackable back to a result.",
        ],
        example: {
          title: "Clothing brand on Meta Ads — questions a Performance Marketer asks",
          lines: [
            "What is the goal? Sales, leads or app installs?",
            "What ROAS are we getting? What is the CPA and CAC?",
            "What will the LTV of a customer be?",
            "What is the Purchase Conversion Value?",
            "RTO coming? From which locations?",
            "Returns coming? What is the issue — size, quality, delivery?",
            "How much revenue is the company actually making?",
          ],
        },
      },
      {
        id: "lead-gen-mindset",
        h: "Lead Generation: the full journey",
        p: [
          "As a Performance Marketer you must know the process from ==generating leads to nurturing leads==, not just how to get a form filled.",
        ],
        example: {
          title: "Real estate client",
          lines: [
            "Leads from Instant Form or bookings from the website? Decide first.",
            "Website bookings → set up custom events so every booking is tracked.",
            "How fast does the sales team call the lead? Minutes, not days.",
            "Team says 'leads are bad'? Ask for remarks: budget mismatch, wrong location, not reachable?",
            "Use those remarks to fix targeting, form questions and the offer.",
          ],
        },
        hinglish: "Lead aa gayi matlab kaam khatam nahi hua. Lead se sale tak ka safar aapki zimmedari hai.",
      },
      {
        id: "usp",
        h: "USP — Unique Selling Proposition",
        p: [
          "USP is the ==special thing about your product or service== that makes customers choose you over competitors.",
          "One line test: ==Why should a customer buy from you instead of someone else?==",
        ],
        sketch: "uspTshirts",
        example: {
          title: "Example 1 — Clothing brand",
          lines: [
            "Brand A: sells T-shirts for ₹799.",
            "Brand B: premium T-shirts that don't lose their shape after washing.",
            "USP of Brand B → long-lasting shape and quality.",
          ],
        },
        example2: {
          title: "Example 2 — Marketing agency",
          lines: [
            "Agency A: provides Meta Ads services.",
            "Agency B: helps D2C clothing brands improve ROAS through conversion-focused campaigns.",
            "Agency B has a clear USP — a niche + a measurable promise.",
          ],
        },
      },
      {
        id: "find-usp",
        h: "How to find a USP for your business",
        steps: [
          { t: "What makes us different?", d: "Price, quality or service? Pick the one you can genuinely own." },
          { t: "Why do customers choose us?", d: "What problem do we solve better than competitors? Green tea brands didn't sell 'tea' — they sold weight loss." },
          { t: "Can we prove it?", d: "Customer reviews, product testing, results, or a genuine guarantee." },
        ],
        hinglish: "USP woh nahi jo aap bolo, USP woh hai jo customer ko yaad rahe — aur jise aap prove kar sako.",
      },
      {
        id: "dm-vs-pm",
        h: "Digital Marketing vs Performance Marketing",
        table: {
          head: ["Basis", "Digital Marketing", "Performance Marketing"],
          rows: [
            ["Meaning", "Promoting a business online", "Marketing focused on measurable results"],
            ["Main goal", "Awareness, engagement, traffic, leads, sales", "Specific measurable outcomes"],
            ["Channels", "SEO, social media, email, ads, content", "Mostly paid ads and other measurable campaigns"],
            ["Measurement", "Reach, followers, traffic, leads, sales", "CPA, CPL, ROAS, conversions"],
            ["Payment", "Can be paid or organic", "Usually paid, with results tracked"],
            ["Example", "Posting Instagram Reels", "Running Meta Ads to generate purchases"],
          ],
        },
        compare: true,
        points: [
          "Digital marketing = promoting your business online.",
          "Performance marketing = focusing on measurable results from your marketing.",
        ],
      },
      {
        id: "audience",
        h: "Target audience, USP and offer",
        p: [
          "Before any ad goes live, three things must be in your head: ==who== you are targeting, ==why== they should pick you, and ==what offer== makes them act now.",
        ],
        sketch: "radius",
        example: {
          title: "Cafe business",
          lines: [
            "Audience: people within 4–5 km of the cafe. Nobody drives 25 km for a coffee.",
            "USP: 'only cafe in the area with a rooftop + live music on Fridays'.",
            "Offer: 'Free dessert with any 2 coffees, this weekend only'.",
          ],
        },
        hinglish: "Galat audience ko sahi ad dikhana bhi budget waste hi hai.",
      },
    ],
    takeaways: [
      "Performance Marketing = revenue + profit focus, not just reach.",
      "USP = why you, not them. Make sure you can prove it.",
      "Audience + USP + Offer must be clear before spending ₹1.",
    ],
    quiz: [
      { q: "Ads spend ₹1,00,000, revenue ₹3,50,000. What is this called?", options: ["Brand awareness", "Performance Marketing", "Organic growth"], answer: 1, why: "You are tracking revenue against spend — that is performance marketing." },
      { q: "Which is a strong USP?", options: ["We sell T-shirts", "T-shirts that keep their shape after 50 washes", "Best T-shirts ever"], answer: 1, why: "Specific, different and provable." },
      { q: "For a local cafe, the best targeting radius is roughly…", options: ["4–5 km", "50 km", "Whole country"], answer: 0, why: "Local businesses need people who can actually walk in." },
    ],
  },
  {
    id: 2,
    slug: "performance-metrics",
    title: "Performance Marketing Metrics",
    color: "green",
    tagline: "Numbers bolte hain. Formula seekho, profit banao.",
    summary: "ROAS, ROI, CPA, CPL, CAC, LTV, MER, CTR, CPM and break-even ROAS.",
    sections: [
      {
        id: "why-metrics",
        h: "Why metrics matter",
        p: [
          "A Performance Marketer is judged by ==numbers, not opinions==. 'Ad achha lag raha hai' is not a report. 'CPA dropped from ₹480 to ₹310' is.",
        ],
        sketch: "funnelNumbers",
        hinglish: "Har funnel step ka ek number hota hai — impression se purchase tak.",
      },
      {
        id: "money-metrics",
        h: "The money metrics",
        formulas: [
          { name: "ROAS", full: "Return on Ad Spend", formula: "Revenue from ads ÷ Ad spend", example: "₹50,000 ÷ ₹10,000 = 5x" },
          { name: "ROI", full: "Return on Investment", formula: "(Profit − Total cost) ÷ Total cost × 100", example: "Cost ₹40k, profit back ₹60k → 50%" },
          { name: "MER", full: "Marketing Efficiency Ratio", formula: "Total revenue ÷ Total marketing spend", example: "₹10L ÷ ₹2.5L = 4" },
          { name: "BE-ROAS", full: "Break-even ROAS", formula: "1 ÷ Gross margin %", example: "Margin 40% → 1 ÷ 0.4 = 2.5x" },
        ],
        hinglish: "ROAS 5x ka matlab ₹5 ka sale, ₹5 ka profit nahi! Product cost, shipping, RTO sab minus hoga.",
      },
      {
        id: "cost-metrics",
        h: "The cost metrics",
        formulas: [
          { name: "CPA", full: "Cost per Acquisition", formula: "Ad spend ÷ Conversions", example: "₹20,000 ÷ 50 orders = ₹400" },
          { name: "CPL", full: "Cost per Lead", formula: "Ad spend ÷ Leads", example: "₹15,000 ÷ 100 leads = ₹150" },
          { name: "CAC", full: "Customer Acquisition Cost", formula: "Total sales + marketing cost ÷ New customers", example: "₹1L ÷ 200 = ₹500" },
          { name: "CPM", full: "Cost per 1,000 Impressions", formula: "Ad spend ÷ Impressions × 1000", example: "₹5,000 ÷ 2,00,000 × 1000 = ₹25" },
        ],
        p: ["CPA usually counts only ad spend. ==CAC is the bigger picture== — salaries, tools, agency fees, everything spent to win a customer."],
      },
      {
        id: "engagement-metrics",
        h: "Funnel metrics",
        formulas: [
          { name: "CTR", full: "Click-through Rate", formula: "Clicks ÷ Impressions × 100", example: "500 ÷ 50,000 = 1%" },
          { name: "CPC", full: "Cost per Click", formula: "Ad spend ÷ Clicks", example: "₹5,000 ÷ 500 = ₹10" },
          { name: "CVR", full: "Conversion Rate", formula: "Conversions ÷ Clicks × 100", example: "25 ÷ 500 = 5%" },
          { name: "AOV", full: "Average Order Value", formula: "Revenue ÷ Number of orders", example: "₹60,000 ÷ 50 = ₹1,200" },
        ],
      },
      {
        id: "ltv",
        h: "LTV — Lifetime Value",
        p: [
          "LTV = how much a customer is worth over the whole relationship, not just the first order.",
          "==LTV = AOV × Purchases per year × Years as customer==",
        ],
        sketch: "ltvStack",
        example: {
          title: "Skincare brand",
          lines: [
            "AOV ₹800, buys 4 times a year, stays 2 years.",
            "LTV = 800 × 4 × 2 = ₹6,400.",
            "So paying ₹600 CAC on the first order is fine — even if the first order looks unprofitable.",
          ],
        },
        hinglish: "Healthy rule of thumb: LTV : CAC kam se kam 3 : 1 hona chahiye.",
      },
      {
        id: "rto",
        h: "RTO and returns — the hidden leak",
        p: [
          "==RTO (Return to Origin)== = the order went out but came back undelivered, mostly with COD. You paid for the ad, shipping both ways, and got zero revenue.",
        ],
        points: [
          "Check RTO % by pincode/state — pause or exclude high-RTO locations.",
          "Push prepaid with a small discount.",
          "Confirm COD orders on WhatsApp/call before dispatch.",
          "Returns? Find the reason: size chart, quality, or the ad over-promised.",
        ],
      },
    ],
    takeaways: [
      "ROAS is revenue, not profit. Always know your break-even ROAS.",
      "CPA counts ad spend; CAC counts everything.",
      "LTV tells you how much CAC you can afford.",
    ],
    quiz: [
      { q: "Spend ₹10,000, revenue ₹40,000. ROAS?", options: ["0.25x", "4x", "40%"], answer: 1, why: "40,000 ÷ 10,000 = 4x." },
      { q: "Gross margin is 50%. Break-even ROAS is…", options: ["2x", "5x", "0.5x"], answer: 0, why: "1 ÷ 0.5 = 2x. Below 2x you lose money." },
      { q: "Which metric includes salaries and tools?", options: ["CPA", "CPC", "CAC"], answer: 2, why: "CAC counts the full cost of acquiring a customer." },
    ],
  },
  {
    id: 3,
    slug: "meta-ads",
    title: "Meta Ads",
    color: "blue",
    tagline: "Facebook & Instagram ads — setup se scaling tak.",
    summary: "Business portfolio setup, objectives, campaign structure, audiences, learning phase.",
    sections: [
      {
        id: "setup",
        h: "Agency-style account setup",
        steps: [
          { t: "Business Portfolio (Business Manager)", d: "The home for everything. Client owns it; the agency gets partner access — never ask for personal login." },
          { t: "Ad account + payment", d: "Correct currency and time zone from day one — they can't be changed later." },
          { t: "Page + Instagram account", d: "Connect both so ads can run from the brand's identity." },
          { t: "Pixel / Dataset + domain verification", d: "Pixel tracks website actions; domain verification proves you own the site." },
        ],
        hinglish: "Setup galat hua toh baad mein tracking aur scaling dono rote hain.",
      },
      {
        id: "structure",
        h: "Campaign → Ad set → Ad",
        sketch: "metaTree",
        table: {
          head: ["Level", "What you decide", "Example"],
          rows: [
            ["Campaign", "Objective (+ budget if Advantage campaign budget)", "Sales"],
            ["Ad set", "Audience, placements, budget, schedule, optimisation event", "Women 22–35, Mumbai, Purchase"],
            ["Ad", "Creative, copy, CTA, link", "Reel + 'Shop Now'"],
          ],
        },
      },
      {
        id: "objectives",
        h: "Pick the right objective",
        p: ["Meta has six objectives. ==Meta optimises for exactly what you ask for== — ask for traffic and you get clickers, not buyers."],
        chips: ["Awareness", "Traffic", "Engagement", "Leads", "App promotion", "Sales"],
        example: {
          title: "Quick rule",
          lines: ["Selling online → Sales (optimise for Purchase).", "Real estate / coaching → Leads (Instant Form or website lead event).", "New brand launch → Awareness for reach, then retarget."],
        },
      },
      {
        id: "audiences",
        h: "Audiences",
        points: [
          "==Core / saved audience== — age, location, interests.",
          "==Custom audience== — your website visitors, video viewers, customer list.",
          "==Lookalike== — new people similar to your best customers.",
          "==Advantage+ audience== — let Meta's AI find buyers, with your inputs as suggestions.",
        ],
        hinglish: "Aaj kal creative hi targeting hai — broad audience + strong creative bahut baar best kaam karta hai.",
      },
      {
        id: "learning",
        h: "Learning phase",
        p: [
          "After launch or a big edit, Meta explores who converts. It exits learning after roughly ==50 optimisation events in 7 days== per ad set.",
        ],
        points: ["Don't edit the ad set every day — each big change resets learning.", "Budget too small for 50 events? Optimise for a higher-funnel event like Add to Cart, or merge ad sets."],
      },
    ],
    takeaways: ["Client owns the assets; agency gets partner access.", "Objective decides who Meta finds for you.", "Respect the learning phase."],
    quiz: [
      { q: "Where do you choose audience and placements?", options: ["Campaign", "Ad set", "Ad"], answer: 1, why: "Targeting lives at the ad set level." },
      { q: "D2C store wants purchases. Objective?", options: ["Traffic", "Engagement", "Sales"], answer: 2, why: "Sales objective optimised for Purchase." },
      { q: "Learning phase usually needs about…", options: ["5 events/week", "50 events/week", "500 events/day"], answer: 1, why: "~50 optimisation events in 7 days." },
    ],
  },
  {
    id: 4,
    slug: "google-ads",
    title: "Google Ads",
    color: "orange",
    tagline: "Jo already search kar raha hai, use pakdo.",
    summary: "Campaign types, keyword match types, Quality Score, bidding and advertiser verification.",
    sections: [
      {
        id: "intent",
        h: "Intent vs interruption",
        p: [
          "On Meta you ==interrupt== people scrolling. On Google Search people ==ask== — 'best running shoes under 3000'. That is high intent.",
        ],
        sketch: "serp",
        hinglish: "Google pe customer khud haath utha ke bolta hai 'mujhe chahiye'.",
      },
      {
        id: "types",
        h: "Campaign types",
        table: {
          head: ["Type", "Where it shows", "Best for"],
          rows: [
            ["Search", "Google search results", "High-intent leads and sales"],
            ["Performance Max", "All Google inventory", "E-commerce with a good feed and tracking"],
            ["Shopping", "Product listings with price", "Online stores (needs Merchant Center)"],
            ["Display", "Websites and apps", "Remarketing, awareness"],
            ["Video / Demand Gen", "YouTube, Discover, Gmail", "Awareness and consideration"],
          ],
        },
      },
      {
        id: "match",
        h: "Keyword match types",
        table: {
          head: ["Match", "Written as", "Can match"],
          rows: [
            ["Broad", "running shoes", "marathon training tips, sneakers sale"],
            ["Phrase", "\"running shoes\"", "best running shoes for women"],
            ["Exact", "[running shoes]", "running shoes, shoes for running"],
          ],
        },
        p: ["Add ==negative keywords== (free, job, second hand, DIY) so you stop paying for useless searches."],
      },
      {
        id: "qs",
        h: "Quality Score & Ad Rank",
        p: ["Quality Score (1–10) is built from three things. Higher score = ==lower CPC and better position==."],
        steps: [
          { t: "Expected CTR", d: "Will people click this ad for this keyword?" },
          { t: "Ad relevance", d: "Does the ad copy match the keyword?" },
          { t: "Landing page experience", d: "Does the page deliver what the ad promised, fast?" },
        ],
      },
      {
        id: "bidding",
        h: "Bidding strategies",
        points: [
          "==Maximise clicks== — new account, gather data.",
          "==Maximise conversions== — once tracking is solid.",
          "==Target CPA== — you know the cost per lead you can afford.",
          "==Target ROAS / Max conversion value== — e-commerce with revenue tracking.",
        ],
      },
      {
        id: "verification",
        h: "Advertiser verification",
        p: ["Google asks advertisers to verify identity — business documents (or ID for individuals) matching the payment profile. ==Keep legal name, documents and billing details identical==, or ads can get paused."],
      },
    ],
    takeaways: ["Search = high intent, so keywords + negatives matter most.", "Quality Score lowers your cost.", "Choose bidding by how much conversion data you have."],
    quiz: [
      { q: "Which match type is most restrictive?", options: ["Broad", "Phrase", "Exact"], answer: 2, why: "Exact matches the same meaning only." },
      { q: "Not part of Quality Score?", options: ["Expected CTR", "Follower count", "Landing page experience"], answer: 1, why: "Followers have nothing to do with it." },
      { q: "Shopping campaigns need…", options: ["Merchant Center", "A WhatsApp number", "An Instagram page"], answer: 0, why: "Products come from your Merchant Center feed." },
    ],
  },
  {
    id: 5,
    slug: "tracking-analytics",
    title: "Tracking & Analytics",
    color: "purple",
    tagline: "Tracking ke bina ads chalana = andhere mein teer.",
    summary: "GTM, GA4, Meta Pixel, Conversions API, UTMs, Hotjar and Clarity.",
    sections: [
      {
        id: "why-tracking",
        h: "Why tracking first",
        p: ["Ad platforms optimise on the data you send. ==Wrong data = wrong optimisation==. A missing Purchase event means Meta can't find buyers."],
        sketch: "trackingFlow",
      },
      {
        id: "tools",
        h: "The toolkit",
        formulas: [
          { name: "GTM", full: "Google Tag Manager", formula: "Tags + Triggers + Variables", example: "Fire GA4 purchase tag on thank-you page" },
          { name: "GA4", full: "Google Analytics 4", formula: "Everything is an event", example: "page_view, add_to_cart, purchase" },
          { name: "Pixel", full: "Meta Pixel", formula: "Browser-side events", example: "ViewContent, AddToCart, Purchase" },
          { name: "CAPI", full: "Conversions API", formula: "Server-side events", example: "Survives ad blockers & iOS limits" },
        ],
        hinglish: "Pixel + CAPI dono lagao, aur same event_id bhejo taaki Meta duplicate count na kare.",
      },
      {
        id: "events",
        h: "Standard e-commerce events",
        chips: ["PageView", "ViewContent", "AddToCart", "InitiateCheckout", "AddPaymentInfo", "Purchase", "Lead"],
        p: ["Always send ==value and currency== with Purchase so ROAS can be calculated. For bookings or forms not covered, create a ==custom event== (e.g. SiteVisitBooked)."],
      },
      {
        id: "utm",
        h: "UTM parameters",
        p: ["Add UTMs to every ad link so GA4 knows exactly which campaign brought the visit."],
        code: "?utm_source=meta&utm_medium=paid_social&utm_campaign=diwali_sale&utm_content=reel_01",
      },
      {
        id: "behaviour",
        h: "Hotjar & Microsoft Clarity",
        points: ["==Heatmaps== — where people click and how far they scroll.", "==Session recordings== — watch real users struggle with your form.", "Use them to answer 'traffic aa raha hai, conversion kyun nahi?'"],
      },
    ],
    takeaways: ["Set up tracking before spending.", "Pixel + CAPI with deduplication.", "UTMs on every link; Clarity to see behaviour."],
    quiz: [
      { q: "CAPI sends events from…", options: ["The browser", "Your server", "Instagram app"], answer: 1, why: "Conversions API is server-side." },
      { q: "In GTM, what decides WHEN a tag fires?", options: ["Trigger", "Variable", "Container"], answer: 0, why: "Triggers define when tags fire." },
      { q: "Purchase event must include…", options: ["Value & currency", "Page colour", "Follower count"], answer: 0, why: "Needed to calculate ROAS." },
    ],
  },
];
